import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, generalAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './PostJob.css';

export default function PostJob() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', requirements: '', benefits: '',
    job_type: 'full_time', location: '', is_remote: false,
    salary_min: '', salary_max: '', salary_currency: 'SAR',
    sport_category_id: '', job_role_id: '', experience_years: '',
    deadline: '', positions_count: 1
  });

  useEffect(() => {
    if (user?.user_type !== 'organization') {
      toast.error('هذه الصفحة للمؤسسات فقط');
      navigate('/dashboard');
      return;
    }
    Promise.all([generalAPI.getCategories(), generalAPI.getRoles()])
      .then(([cats, rls]) => {
        setCategories(cats.data.data || []);
        setRoles(rls.data.data || []);
      });
  }, []);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.title || !form.description || !form.sport_category_id) {
      toast.error('يرجى ملء الحقول الإلزامية');
      return;
    }
    setSubmitting(true);
    try {
      const res = await jobsAPI.create(form);
      toast.success('تم نشر الوظيفة بنجاح!');
      navigate(`/jobs/${res.data.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل نشر الوظيفة');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="post-page">
      <div className="post-page-header">
        <div className="post-header-content">
          <h1>نشر وظيفة جديدة</h1>
          <p>أضف تفاصيل الوظيفة للوصول إلى أفضل الكفاءات الرياضية</p>
        </div>
      </div>

      <div className="post-form-container">
        <form onSubmit={handleSubmit} className="post-form">
          <div className="form-section">
            <h2 className="form-section-title">المعلومات الأساسية</h2>
            <div className="form-grid">
              <div className="form-group full-width">
                <label className="form-label">عنوان الوظيفة *</label>
                <input type="text" name="title" className="form-input"
                  placeholder="مثال: مدرب كرة قدم لفئة الشباب"
                  value={form.title} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label className="form-label">التصنيف الرياضي *</label>
                <select name="sport_category_id" className="form-input"
                  value={form.sport_category_id} onChange={handleChange} required>
                  <option value="">اختر الرياضة</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name_ar || c.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">المسمى الوظيفي</label>
                <select name="job_role_id" className="form-input"
                  value={form.job_role_id} onChange={handleChange}>
                  <option value="">اختر المسمى</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name_ar || r.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">نوع التوظيف</label>
                <select name="job_type" className="form-input" value={form.job_type} onChange={handleChange}>
                  <option value="full_time">دوام كامل</option>
                  <option value="part_time">دوام جزئي</option>
                  <option value="contract">تعاقد</option>
                  <option value="freelance">عمل حر</option>
                  <option value="internship">تدريب</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">الموقع</label>
                <input type="text" name="location" className="form-input"
                  placeholder="المدينة، الدولة"
                  value={form.location} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">سنوات الخبرة المطلوبة</label>
                <input type="number" name="experience_years" className="form-input"
                  placeholder="0" min="0" max="30"
                  value={form.experience_years} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">عدد الشواغر</label>
                <input type="number" name="positions_count" className="form-input"
                  min="1" value={form.positions_count} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">آخر موعد للتقديم</label>
                <input type="date" name="deadline" className="form-input"
                  value={form.deadline} onChange={handleChange} />
              </div>
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input type="checkbox" name="is_remote" checked={form.is_remote} onChange={handleChange} />
                  <span className="checkbox-text">عمل عن بُعد</span>
                </label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="form-section-title">الراتب والمزايا</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">الحد الأدنى للراتب</label>
                <input type="number" name="salary_min" className="form-input"
                  placeholder="3000" value={form.salary_min} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">الحد الأقصى للراتب</label>
                <input type="number" name="salary_max" className="form-input"
                  placeholder="8000" value={form.salary_max} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label className="form-label">العملة</label>
                <select name="salary_currency" className="form-input"
                  value={form.salary_currency} onChange={handleChange}>
                  <option value="SAR">ريال سعودي</option>
                  <option value="AED">درهم إماراتي</option>
                  <option value="KWD">دينار كويتي</option>
                  <option value="QAR">ريال قطري</option>
                  <option value="USD">دولار أمريكي</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="form-section-title">تفاصيل الوظيفة</h2>
            <div className="form-group">
              <label className="form-label">وصف الوظيفة *</label>
              <textarea name="description" className="form-input" rows="6"
                placeholder="اشرح مهام الوظيفة والمسؤوليات..."
                value={form.description} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label className="form-label">المتطلبات والمؤهلات</label>
              <textarea name="requirements" className="form-input" rows="5"
                placeholder="المؤهلات العلمية، الشهادات، الخبرات المطلوبة..."
                value={form.requirements} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label className="form-label">المزايا والحوافز</label>
              <textarea name="benefits" className="form-input" rows="4"
                placeholder="التأمين الصحي، الإجازات، مزايا إضافية..."
                value={form.benefits} onChange={handleChange} />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate(-1)}>إلغاء</button>
            <button type="submit" className="btn btn-primary btn-lg" disabled={submitting}>
              {submitting ? 'جاري النشر...' : '🚀 نشر الوظيفة'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
